import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader, ConfirmDialog } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem, MinimizedModalStrip } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { projectApi, ProjectDto, ProjectUpsertRequest, StatusHistoryEntryDto } from 'api/services/timeTrackerApi';
import { useUnsavedChangesGuard } from 'hooks/useUnsavedChangesGuard';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

const blankForm: ProjectUpsertRequest = { name: '', description: '', isActive: true };

interface ProjectMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ProjectMasterScreen: React.FC<ProjectMasterScreenProps> = ({ tabId, moduleName }) => {
  const { minimize, isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const [rows, setRows] = useState<ProjectDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'' | 'Open' | 'Closed'>('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectDto | null>(null);
  const [form, setForm] = useState<ProjectUpsertRequest>(blankForm);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ProjectDto | null>(null);

  // Close / Reopen confirmation
  const [closeOpen, setCloseOpen]         = useState(false);
  const [reopenOpen, setReopenOpen]       = useState(false);
  const [statusBusy, setStatusBusy]       = useState(false);
  const [pendingStatus, setPendingStatus] = useState<ProjectDto | null>(null);

  // History modal
  const [historyOpen, setHistoryOpen]       = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTarget, setHistoryTarget]   = useState<ProjectDto | null>(null);
  const [historyData, setHistoryData]       = useState<StatusHistoryEntryDto[]>([]);

  const formGuard = useUnsavedChangesGuard(formOpen, form, () => !saving && setFormOpen(false));

  // Minimize plumbing
  const formRef = useRef(form);           formRef.current = form;
  const editingRef = useRef(editing);     editingRef.current = editing;
  const initialSnapshotRef = useRef<string>('');
  const isRestoreRef = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!formOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    initialSnapshotRef.current = JSON.stringify(form);
  }, [formOpen]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentForm = { ...formRef.current };
    const currentEditing = editingRef.current;
    const snapshot = initialSnapshotRef.current;
    const modalId = currentEditing
      ? `${tabId}-form-modal-edit-${currentEditing.id}`
      : `${tabId}-form-modal-create-1`;
    const nameStr = currentForm.name?.trim();
    const title = currentEditing
      ? `Edit - ${currentEditing.name}`
      : (nameStr ? `Add - ${nameStr} (1)` : `Add New (1)`);
    const isDirty = snapshot ? JSON.stringify(currentForm) !== snapshot : false;
    minimize({
      id: modalId, title, moduleName, tabId, isDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setEditing(currentEditing);
        setForm(currentForm);
        initialSnapshotRef.current = snapshot;
        setFormOpen(true);
      },
    });
    setFormOpen(false);
  }, [tabId, moduleName, minimize]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectApi.list({
        page: page + 1,
        pageSize,
        searchTerm: searchQuery || undefined,
        includeInactive: includeInactive || undefined,
        status: filterStatus || undefined,
      });
      setRows(res.items);
      setTotalCount(res.totalCount);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err.message || 'Failed to load projects.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, includeInactive, filterStatus]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [load]);

  const openAdd = useCallback(() => {
    const modalId = tabId ? `${tabId}-form-modal-create-1` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setEditing(null);
    setForm(blankForm);
    setFieldErrors({});
    setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const openEdit = useCallback((row: ProjectDto) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${row.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setEditing(row);
    setForm({ name: row.name, description: row.description ?? '', isActive: row.isActive });
    setFieldErrors({});
    setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.name?.trim()) errs.name = 'Name is required';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const save = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (editing) {
        await projectApi.update(editing.id, { ...form, rowVersion: editing.rowVersion });
        notificationService.toast.success(`Project "${form.name}" updated.`);
      } else {
        await projectApi.create(form);
        notificationService.toast.success(`Project "${form.name}" created.`);
      }
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = useCallback((row: ProjectDto) => {
    setPendingDelete(row);
    setDeleteOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await projectApi.remove(pendingDelete.id, pendingDelete.rowVersion);
      notificationService.toast.success(`"${pendingDelete.name}" deleted.`);
      setDeleteOpen(false);
      setPendingDelete(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, load]);

  // ── Close / Reopen / History handlers ───────────────────────────────
  const requestClose = useCallback((row: ProjectDto) => {
    setPendingStatus(row);
    setCloseOpen(true);
  }, []);

  const requestReopen = useCallback((row: ProjectDto) => {
    setPendingStatus(row);
    setReopenOpen(true);
  }, []);

  const confirmClose = useCallback(async () => {
    if (!pendingStatus) return;
    setStatusBusy(true);
    try {
      await projectApi.close(pendingStatus.id, pendingStatus.rowVersion);
      notificationService.toast.success(`"${pendingStatus.name}" closed.`);
      setCloseOpen(false);
      setPendingStatus(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Close failed.');
    } finally {
      setStatusBusy(false);
    }
  }, [pendingStatus, load]);

  const confirmReopen = useCallback(async () => {
    if (!pendingStatus) return;
    setStatusBusy(true);
    try {
      await projectApi.reopen(pendingStatus.id, pendingStatus.rowVersion);
      notificationService.toast.success(`"${pendingStatus.name}" reopened.`);
      setReopenOpen(false);
      setPendingStatus(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Reopen failed.');
    } finally {
      setStatusBusy(false);
    }
  }, [pendingStatus, load]);

  const viewHistory = useCallback(async (row: ProjectDto) => {
    setHistoryTarget(row);
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const detail = await projectApi.getById(row.id);
      setHistoryData(detail.statusHistory || []);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to load history.');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  type GridRow = ProjectDto & { id: string };
  const columns: SyutGridColumn<GridRow>[] = useMemo(() => [
    { id: 'name',        header: 'Project Name', accessorKey: 'name',        size: 240 },
    { id: 'description', header: 'Description',  accessorKey: 'description', size: 360, enableSorting: false,
      cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'status',      header: 'Status',       accessorKey: 'status',      size: 130,
      cell: ({ row }) => {
        const s = row.original.status;
        const reopenedOnce = (row.original.statusHistory || []).some(h => h.transition === 'Reopened');
        return (
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <span className={`badge ${s === 'Open' ? 'badge-active' : 'badge-inactive'}`}>{s}</span>
            {reopenedOnce && (
              <span title="Has been reopened at least once" style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>⟳</span>
            )}
          </span>
        );
      },
    },
    { id: 'isActive',    header: 'Active',       accessorKey: 'isActive',    size: 100,
      cell: ({ getValue }) => (
        <span className={`badge ${getValue() ? 'badge-active' : 'badge-inactive'}`}>
          {getValue() ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ], []);

  const rowActions = useCallback((row: GridRow): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'edit',    label: 'Edit',          icon: <IconSystem.edit  size={16} />, onClick: () => openEdit(row) },
      { id: 'history', label: 'View history',  icon: <IconSystem.clock size={16} />, onClick: () => viewHistory(row) },
    ];
    if (row.status === 'Open') {
      items.push({ id: 'close', label: 'Close', icon: <IconSystem.lock   size={16} />, onClick: () => requestClose(row), variant: 'danger' });
    } else {
      items.push({ id: 'reopen', label: 'Reopen', icon: <IconSystem.unlock size={16} />, onClick: () => requestReopen(row) });
    }
    items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => requestDelete(row), variant: 'danger' });
    return items;
  }, [openEdit, requestDelete, requestClose, requestReopen, viewHistory]);

  const actions: ActionButton[] = [
    { id: 'add',     icon: <IconSystem.add     size={18} />, label: 'Add Project', onClick: openAdd, tooltip: 'Add new project', variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh',     onClick: load,    tooltip: 'Refresh',          variant: 'icon' },
  ];

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Project Master"
        description="Standalone project list (decoupled from clients)."
        icon={<IconSystem.folder size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search projects…' }}
        filterConfig={{ onClick: () => setFilterOpen(o => !o), isActive: filterOpen || includeInactive }}
        actions={actions}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          <SyutGrid
            data={rows as GridRow[]}
            columns={columns}
            enableRowSelection
            enablePagination
            manualPagination
            totalCount={totalCount}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading…' : 'No projects found.'}
          />
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', color: 'var(--text-muted)' }}>
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Status</label>
                <select
                  className="form-select"
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value as '' | 'Open' | 'Closed'); setPage(0); }}
                  style={{ fontSize: '0.8125rem' }}
                >
                  <option value="">All</option>
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                  <input
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(e) => { setIncludeInactive(e.target.checked); setPage(0); }}
                  />
                  Include inactive projects
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setIncludeInactive(false); setFilterStatus(''); setPage(0); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Done</button>
            </div>
          </div>
        )}
      </div>

      {formOpen && (
        <div className="modal-backdrop" onClick={formGuard.requestClose}>
          <div className="modal-container modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <IconSystem.folder size={16} style={{ marginRight: 8 }} />
                {editing ? 'Edit Project' : 'New Project'}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                {tabId && moduleName && (
                  <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                    <IconSystem.chevronDown size={16} />
                  </button>
                )}
                <button className="modal-close" onClick={formGuard.requestClose}>
                  <IconSystem.x size={18} />
                </button>
              </div>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label className="form-label">Name<span style={{ color: 'var(--danger)' }}> *</span></label>
                <input
                  className={`form-input ${fieldErrors.name ? 'form-field-error' : ''}`}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  maxLength={200}
                  autoFocus
                />
                {fieldErrors.name && <div className="form-error-message">{fieldErrors.name}</div>}
              </div>

              <div className="form-group" style={{ marginTop: '0.875rem' }}>
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={form.description ?? ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="form-group" style={{ marginTop: '0.875rem' }}>
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={form.isActive ? '1' : '0'}
                  onChange={(e) => setForm({ ...form, isActive: e.target.value === '1' })}
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={formGuard.requestClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : (editing ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="Delete Project"
        message={`Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        loadingMessage="Deleting…"
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteOpen(false); setPendingDelete(null); }}
      />

      <ConfirmDialog
        open={closeOpen}
        title="Close Project"
        message={`Close "${pendingStatus?.name}"? You can reopen it later.`}
        confirmLabel="Close project"
        cancelLabel="Cancel"
        variant="danger"
        loading={statusBusy}
        loadingMessage="Closing…"
        onConfirm={confirmClose}
        onCancel={() => { setCloseOpen(false); setPendingStatus(null); }}
      />

      <ConfirmDialog
        open={reopenOpen}
        title="Reopen Project"
        message={`Reopen "${pendingStatus?.name}"?`}
        confirmLabel="Reopen"
        cancelLabel="Cancel"
        variant="default"
        loading={statusBusy}
        loadingMessage="Reopening…"
        onConfirm={confirmReopen}
        onCancel={() => { setReopenOpen(false); setPendingStatus(null); }}
      />

      <ConfirmDialog
        open={formGuard.discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={formGuard.confirmDiscard}
        onCancel={formGuard.cancelDiscard}
      />

      {historyOpen && (
        <div className="modal-backdrop" onClick={() => setHistoryOpen(false)}>
          <div className="modal-container modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <IconSystem.clock size={16} style={{ marginRight: 8 }} />
                Status history — {historyTarget?.name}
              </h2>
              <button className="modal-close" onClick={() => setHistoryOpen(false)}>
                <IconSystem.x size={18} />
              </button>
            </div>
            <div className="modal-content">
              {historyLoading ? (
                <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</div>
              ) : historyData.length === 0 ? (
                <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  No close/reopen events yet. This project has been Open since it was created.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {historyData.map(h => (
                    <div key={h.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
                      padding: '0.625rem 0.75rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0.375rem',
                      background: 'var(--bg-secondary)',
                    }}>
                      <span style={{
                        flexShrink: 0, marginTop: 2,
                        width: 8, height: 8, borderRadius: '50%',
                        background: h.transition === 'Closed' ? 'var(--danger)' : 'var(--primary)',
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                          {h.transition}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          {new Date(h.changedAt).toLocaleString()} · {h.changedByName || h.changedById?.slice(0, 8) || '—'}
                        </div>
                        {h.note && (
                          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                            {h.note}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setHistoryOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectMasterScreen;
